import _ from 'lodash';
import {connect} from 'react-redux';

import {
  FloatingActivity,
  isFloatingActivityAnnotation,
  isFloatingActivityUrl,
} from '../../../../libs/shared/core/src/helpers/annotations/annotationHelpers';
import {
  isLinkedActivityMessageAnnotation,
  maybeFindAnnotationLinkedActivityId,
} from '../../../../libs/shared/core/src/models/api/annotationModel';
import {AnnotationLinkedResourcesEnum} from '../../../../libs/shared/core/src/models/api/annotationTypes';
import {NewPanelId, RViewerPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../srcCommon/helpers/reselectHelpers';
import {makeGetAttachments} from '../../../../srcCommon/inboxes/viewer/activity/comment/commentViewerSelectors';
import {isLinkedConversationActivity} from '../../../models/api/activityModel';
import {isTeammateActivitySource} from '../../../models/api/activitySourceModel';
import {AppState} from '../../../store/appState';
import {
  getActivities,
  getAnnotations,
  getComments,
  getConversations,
} from '../../../store/data/dataSelectors';
import {makeGetMentionableAndCommentAliases} from '../../../store/data/teammates/mentionTeammatesSelectors';
import {getFindTeammate, getUserAlias, getUserId} from '../../../store/data/teammates/teammatesSelectors';
import {getTopics} from '../../../store/data/topics/topicsSelectors';
import {jumpToActivityAsyncAction} from '../../../store/panels/viewer/viewerPanelActions';
import {FloatingActivityDropdown} from './floatingActivityDropdown';

/*
 * Props.
 */

interface FloatingActivityDropdownContainerProps {
  floatingActivities: ReadonlyArray<FloatingActivity>;
  panelId: NewPanelId;
}

/*
 * Selectors.
 */

function getAnnotationUids(_state: AppState, props: FloatingActivityDropdownContainerProps) {
  return props.floatingActivities
    .filter(isFloatingActivityAnnotation)
    .map((activity) => activity.annotationUid);
}

function getActiveUrl(_state: AppState, props: FloatingActivityDropdownContainerProps) {
  return props.floatingActivities.find(isFloatingActivityUrl)?.url;
}

function makeGetActiveAnnotations() {
  return createSelector([getAnnotations, getAnnotationUids], (annotations, annotationUids) =>
    _(annotationUids)
      .map((annotationUid) => annotations[annotationUid])
      .compact()
      .value(),
  );
}

function makeGetTopic(getActiveAnnotations: ReturnType<typeof makeGetActiveAnnotations>) {
  return createSelector([getActiveAnnotations, getTopics], (annotations, topics) => {
    const linkedTopicId = _(annotations)
      .map((a) => {
        if (a.linkedResource.type !== AnnotationLinkedResourcesEnum.TOPIC) {
          return undefined;
        }
        return a.linkedResource.topicId;
      })
      .compact()
      .first();

    if (!linkedTopicId) {
      return undefined;
    }

    return topics[linkedTopicId];
  });
}

function makeGetComment(getActiveAnnotations: ReturnType<typeof makeGetActiveAnnotations>) {
  return createSelector([getActiveAnnotations, getComments], (annotations, comments) => {
    const comment = _(annotations)
      .map((annotation) => {
        if (
          !annotation ||
          annotation.linkedResource.type !== AnnotationLinkedResourcesEnum.COMMENT ||
          !annotation.linkedResource.commentUid
        ) {
          return undefined;
        }

        return comments[annotation.linkedResource.commentUid];
      })
      .compact()
      .first();
    return comment ?? null;
  });
}

function makeGetConversationId(getComment: ReturnType<typeof makeGetComment>) {
  return createSelector([getComment], (comment) => comment?.conversationId);
}

function makeGetNamespace(getConversationId: ReturnType<typeof makeGetConversationId>) {
  return createSelector([getConversationId, getConversations], (conversationId, conversations) =>
    conversationId ? conversations[conversationId]?.namespace : undefined,
  );
}

function makeGetAnnotation(
  getComment: ReturnType<typeof makeGetComment>,
  getLinkedMessagedActivity: ReturnType<typeof makeGetLinkedActivity>,
) {
  return createSelector(
    [getAnnotations, getComment, getLinkedMessagedActivity],
    (annotations, comment, linkedMessageActivity) => {
      if (comment?.annotationUid) {
        return annotations[comment.annotationUid];
      }
      if (linkedMessageActivity?.annotationUid) {
        return annotations[linkedMessageActivity.annotationUid];
      }
      return undefined;
    },
  );
}

/** Get the activity in this conversation that this annotation points to, i.e. the activity to jump to. */
function makeGetLinkedActivity(getActiveAnnotations: ReturnType<typeof makeGetActiveAnnotations>) {
  return createSelector([getActiveAnnotations, getActivities], (activeAnnotations, activities) =>
    _(activeAnnotations)
      .map((annotation) => {
        // Skip unless this is an annotation for a linked conversation.
        if (!isLinkedActivityMessageAnnotation(annotation.linkedResource)) {
          return undefined;
        }

        // Get the activity of the linked conversation, correctly typed.
        const activity = activities[annotation.linkedResource.linkedActivityId];
        if (!activity || !isLinkedConversationActivity(activity)) {
          return undefined;
        }

        return activity;
      })
      .compact()
      .first(),
  );
}

function makeGetAuthor(
  getComment: ReturnType<typeof makeGetComment>,
  getLinkedMessagedActivity: ReturnType<typeof makeGetLinkedActivity>,
) {
  return createSelector(
    [getComment, getLinkedMessagedActivity, getFindTeammate],
    (comment, activity, findTeammate) => {
      const computeAuthorId = () => {
        if (comment) {
          return comment.authorId;
        }
        if (activity && isTeammateActivitySource(activity.source)) {
          return activity.source.teammateId;
        }
        return undefined;
      };

      const authorId = computeAuthorId();
      if (!authorId) {
        return undefined;
      }

      return findTeammate(authorId);
    },
  );
}

const mapStateToProps = makeMapStateToProps<FloatingActivityDropdownContainerProps>()(() => {
  const getActiveAnnotations = makeGetActiveAnnotations();
  const getComment = makeGetComment(getActiveAnnotations);
  const getConversationId = makeGetConversationId(getComment);
  const getNamespace = makeGetNamespace(getConversationId);
  const getTopic = makeGetTopic(getActiveAnnotations);
  const getLinkedActivity = makeGetLinkedActivity(getActiveAnnotations);
  const getAuthor = makeGetAuthor(getComment, getLinkedActivity);

  const getMentionableAndCommentAliases = makeGetMentionableAndCommentAliases(getComment);

  // This is only used in `mergeProps` below:
  const getAnnotation = makeGetAnnotation(getComment, getLinkedActivity);

  return {
    comment: getComment,
    annotation: getAnnotation,
    linkedActivity: getLinkedActivity,
    author: getAuthor,
    userId: getUserId,
    attachments: makeGetAttachments(getComment),
    mentionableAliases: getMentionableAndCommentAliases,
    userAlias: getUserAlias,

    conversationId: getConversationId,
    namespace: getNamespace,
    topic: getTopic,
    activeUrl: getActiveUrl,
  };
});

const mapDispatchToProps = makeMapDispatchToProps<FloatingActivityDropdownContainerProps>()(() => ({
  jumpToActivity: jumpToActivityAsyncAction,
}));

export const FloatingActivityDropdownContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  ({annotation, ...stateProps}, dispatchProps, ownProps) => {
    const {panelId} = ownProps;

    const onJumpToActivityClick = () => {
      // TODO: Fix the behavior of annotations in popup message viewers.
      if (!RViewerPanelId.is(panelId)) {
        return;
      }
      if (!annotation) {
        return;
      }

      const activityId = maybeFindAnnotationLinkedActivityId(annotation);
      if (!activityId) {
        return;
      }

      dispatchProps.jumpToActivity(panelId, {id: activityId});
    };

    return {
      ...ownProps,
      ...stateProps,
      onJumpToActivityClick,
    };
  },
)(FloatingActivityDropdown);
